import { MrfiktivBoundingBoxDtoGen, MrfiktivBoundingBoxViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";

export class BoundingBoxBase implements MrfiktivBoundingBoxViewModelGen {
  /**
   * @inheritdoc
   */
  x: number;

  /**
   * @inheritdoc
   */
  y: number;

  /**
   * @inheritdoc
   */
  x2: number;

  /**
   * @inheritdoc
   */
  y2: number;

  get w(): number {
    return this.x2 - this.x;
  }

  get h(): number {
    return this.y2 - this.y;
  }

  constructor(input?: MrfiktivBoundingBoxViewModelGen | IBoundingBox) {
    if (!input) {
      this.x = 0;
      this.y = 0;
      this.x2 = 0;
      this.y2 = 0;
      return;
    }

    this.x = input.x;
    this.y = input.y;
    this.x2 = input.x2;
    this.y2 = input.y2;

    this.normalize();
  }

  get dto(): MrfiktivBoundingBoxDtoGen {
    return { x: this.x, y: this.y, x2: this.x2, y2: this.y2 };
  }

  /**
   * Create a bounding box from pixel coordinates using normalized (0..1) scale.
   * Previously this multiplied by 100 resulting in 0..100 scale which was inconsistent with the rest of the codebase.
   */
  static fromPx(input: { x: number; y: number; w: number; h: number }, imageW: number, imageH: number) {
    const { x, y, w, h } = input;
    const nx = x / imageW;
    const ny = y / imageH;
    const nx2 = (x + w) / imageW;
    const ny2 = (y + h) / imageH;
    return new BoundingBox({ x: nx, y: ny, x2: nx2, y2: ny2 });
  }

  // Ensure x<=x2 and y<=y2
  normalize() {
    if (this.x > this.x2) {
      const tmp = this.x;
      this.x = this.x2;
      this.x2 = tmp;
    }
    if (this.y > this.y2) {
      const tmp = this.y;
      this.y = this.y2;
      this.y2 = tmp;
    }
  }

  /**
   * Clamp the box within 0..1 keeping width and height constant.
   * If box exceeds left/top, shift right/down; if exceeds right/bottom, shift left/up.
   */
  justifyBorders() {
    const w = this.w;
    const h = this.h;
    // If width/height exceed bounds, shrink to fit (edge case)
    if (w > 1) {
      this.x = 0;
      this.x2 = 1;
    }
    if (h > 1) {
      this.y = 0;
      this.y2 = 1;
    }
    // Clamp horizontally
    if (this.x < 0) {
      this.x = 0;
      this.x2 = w;
    }
    if (this.x2 > 1) {
      this.x2 = 1;
      this.x = 1 - w;
    }
    // Clamp vertically
    if (this.y < 0) {
      this.y = 0;
      this.y2 = h;
    }
    if (this.y2 > 1) {
      this.y2 = 1;
      this.y = 1 - h;
    }
  }

  getCenter(): { x: number; y: number } {
    return { x: (this.x + this.x2) / 2, y: (this.y + this.y2) / 2 };
  }

  isHit(px: number, py: number): boolean {
    return px >= this.x && px < this.x2 && py >= this.y && py < this.y2;
  }

  /**
   * Convert normalized bounding box (0..1) to pixel coordinates for a given image size.
   */
  toPixelDto(imageW?: number, imageH?: number) {
    if (typeof imageW === "number" && typeof imageH === "number") {
      const xPx = this.x * imageW;
      const yPx = this.y * imageH;
      const wPx = this.w * imageW;
      const hPx = this.h * imageH;
      return { x: xPx, y: yPx, w: wPx, h: hPx };
    }
    return this.dto;
  }

  /**
   * Return pixel edge coordinates for the box given image size.
   */
  edgesPx(
    imageW: number,
    imageH: number
  ): { left: number; top: number; right: number; bottom: number; width: number; height: number } {
    const left = this.x * imageW;
    const top = this.y * imageH;
    const right = this.x2 * imageW;
    const bottom = this.y2 * imageH;
    return { left, top, right, bottom, width: right - left, height: bottom - top };
  }

  /**
   * Create a bounding box from pixel edge coordinates (left, top, right, bottom)
   */
  static fromEdgesPx(
    edges: { left: number; top: number; right: number; bottom: number },
    imageW: number,
    imageH: number
  ) {
    const { left, top, right, bottom } = edges;
    const nx = imageW ? left / imageW : 0;
    const ny = imageH ? top / imageH : 0;
    const nx2 = imageW ? right / imageW : 0;
    const ny2 = imageH ? bottom / imageH : 0;
    return new BoundingBox({ x: nx, y: ny, x2: nx2, y2: ny2 });
  }

  /**
   * Hit test using pixel coordinates and image size.
   */
  isHitPx(xPx: number, yPx: number, imageW: number, imageH: number): boolean {
    if (!imageW || !imageH) return false;
    const nx = xPx / imageW;
    const ny = yPx / imageH;
    return this.isHit(nx, ny);
  }
}

type IBoundingBox = BoundingBoxBase;
const BoundingBox = BoundingBoxBase;

export { IBoundingBox, BoundingBox };
